import React from 'react';
import { Box, Typography, LinearProgress, Tooltip } from '@mui/material';
import { Battery0Bar, Battery1Bar, Battery2Bar, BatteryFull, BatteryAlert } from '@mui/icons-material';

interface ChargerHealthIndicatorProps {
  healthPercentage?: number;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const ChargerHealthIndicator: React.FC<ChargerHealthIndicatorProps> = ({
  healthPercentage = 0,
  showLabel = true,
  size = 'medium',
}) => {
  const getHealthColor = (health: number) => {
    if (health >= 90) return 'success';
    if (health >= 75) return 'primary';
    if (health >= 60) return 'warning';
    return 'error';
  };

  const getHealthIcon = (health: number) => {
    if (health >= 90) return <BatteryFull color="success" />;
    if (health >= 75) return <Battery2Bar color="primary" />;
    if (health >= 60) return <Battery1Bar color="warning" />;
    if (health > 0) return <Battery0Bar color="error" />;
    return <BatteryAlert color="disabled" />;
  };

  const getHealthLabel = (health: number) => {
    if (health >= 90) return 'Excellent';
    if (health >= 75) return 'Good';
    if (health >= 60) return 'Fair';
    return 'Poor';
  };

  const barHeight = size === 'small' ? 4 : size === 'medium' ? 6 : 8;

  return (
    <Tooltip title={`Health: ${healthPercentage}%`} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
        {getHealthIcon(healthPercentage)}
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={healthPercentage}
            color={getHealthColor(healthPercentage) as any}
            sx={{
              height: barHeight,
              borderRadius: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            }}
          />
          {showLabel && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
              {getHealthLabel(healthPercentage)} ({healthPercentage}%)
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};
